import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box, IconButton, Tooltip, Typography } from "@mui/joy";
import { motion } from "framer-motion";
import CloseRoundedIcon        from "@mui/icons-material/CloseRounded";
import AddRoundedIcon          from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon       from "@mui/icons-material/RemoveRounded";
import RotateLeftRoundedIcon   from "@mui/icons-material/RotateLeftRounded";
import RotateRightRoundedIcon  from "@mui/icons-material/RotateRightRounded";
import RestartAltRoundedIcon   from "@mui/icons-material/RestartAltRounded";
import DownloadRoundedIcon     from "@mui/icons-material/DownloadRounded";
import ChevronLeftRoundedIcon  from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

const MIN_SCALE = 0.25;
const MAX_SCALE = 6;

// images: [{ url, name }], index — с какого фото открыть, onClose — закрыть.
export default function ImageViewer({ images = [], index = 0, onClose }) {
    const [i, setI]         = useState(index);
    const [scale, setScale] = useState(1);
    const [rot, setRot]     = useState(0);
    const [pos, setPos]     = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const drag = useRef({ active: false, sx: 0, sy: 0, ox: 0, oy: 0 });

    const cur = images[i];

    const reset   = useCallback(() => { setScale(1); setRot(0); setPos({ x: 0, y: 0 }); }, []);
    const zoomIn  = useCallback(() => setScale(s => Math.min(+(s + 0.25).toFixed(2), MAX_SCALE)), []);
    const zoomOut = useCallback(() => setScale(s => Math.max(+(s - 0.25).toFixed(2), MIN_SCALE)), []);
    const rotateL = useCallback(() => setRot(r => r - 90), []);
    const rotateR = useCallback(() => setRot(r => r + 90), []);
    const go = useCallback((delta) => {
        if (images.length < 2) return;
        setI(v => (v + delta + images.length) % images.length);
        reset();
    }, [images.length, reset]);

    // Клавиатура: Esc, +/-, стрелки, R — поворот
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "+" || e.key === "=") zoomIn();
            else if (e.key === "-" || e.key === "_") zoomOut();
            else if (e.key === "ArrowLeft") go(-1);
            else if (e.key === "ArrowRight") go(1);
            else if (e.key.toLowerCase() === "r") rotateR();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, zoomIn, zoomOut, go, rotateR]);

    // Блокируем прокрутку фона, пока открыт
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    const onWheel = (e) => { (e.deltaY < 0 ? zoomIn : zoomOut)(); };

    // Перетаскивание (панорамирование) при увеличении
    const onPointerDown = (e) => {
        e.stopPropagation();
        drag.current = { active: true, sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
        setDragging(true);
    };
    const onPointerMove = (e) => {
        if (!drag.current.active) return;
        setPos({
            x: drag.current.ox + (e.clientX - drag.current.sx),
            y: drag.current.oy + (e.clientY - drag.current.sy),
        });
    };
    const endDrag = () => { drag.current.active = false; setDragging(false); };

    if (!cur) return null;

    const ctrlSx = {
        "--IconButton-size": "38px", color: "#fff", bgcolor: "rgba(255,255,255,0.08)",
        borderRadius: 10, "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
    };

    const node = (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            onWheel={onWheel}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            style={{
                position: "fixed", inset: 0, zIndex: 13000, background: "rgba(10,10,14,0.93)",
                display: "flex", alignItems: "center", justifyContent: "center",
                userSelect: "none", touchAction: "none", overflow: "hidden",
            }}
        >
            {/* Имя файла + счётчик */}
            <Box sx={{ position: "absolute", top: 14, left: 18, color: "rgba(255,255,255,0.85)", pointerEvents: "none" }}>
                <Typography sx={{ color: "inherit", fontSize: "0.9rem", fontWeight: 500 }}>
                    {cur.name || "Изображение"}
                </Typography>
                {images.length > 1 && (
                    <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>
                        {i + 1} / {images.length}
                    </Typography>
                )}
            </Box>

            {/* Закрыть */}
            <IconButton onClick={onClose} sx={{ ...ctrlSx, position: "absolute", top: 12, right: 14 }}>
                <CloseRoundedIcon sx={{ color: 'white' }} />
            </IconButton>

            {/* Стрелки листания */}
            {images.length > 1 && (
                <>
                    <IconButton onClick={() => go(-1)}
                        sx={{ ...ctrlSx, position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}>
                        <ChevronLeftRoundedIcon sx={{ color: 'white' }} />
                    </IconButton>
                    <IconButton onClick={() => go(1)}
                        sx={{ ...ctrlSx, position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)" }}>
                        <ChevronRightRoundedIcon sx={{ color: 'white' }} />
                    </IconButton>
                </>
            )}

            {/* Само изображение */}
            <Box component="img" src={cur.url} alt={cur.name || ""}
                draggable={false}
                onPointerDown={onPointerDown}
                onDoubleClick={() => (scale === 1 ? setScale(2) : reset())}
                sx={{
                    maxWidth: "90vw", maxHeight: "82vh", objectFit: "contain",
                    transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale}) rotate(${rot}deg)`,
                    transition: dragging ? "none" : "transform 0.15s ease",
                    cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
                    borderRadius: 6, boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
                }}
            />

            {/* Нижняя панель управления */}
            <Box
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
                    display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5,
                    bgcolor: "rgba(20,20,26,0.85)", borderRadius: 14, backdropFilter: "blur(6px)" }}
            >
                <Tooltip title="Уменьшить" size="sm" sx={{ zIndex: 13001 }}>
                    <IconButton onClick={zoomOut} sx={ctrlSx}><RemoveRoundedIcon sx={{ color: 'white' }} /></IconButton>
                </Tooltip>
                <Typography sx={{ color: "#fff", fontSize: "0.8rem", minWidth: 46, textAlign: "center" }}>
                    {Math.round(scale * 100)}%
                </Typography>
                <Tooltip title="Увеличить" size="sm" sx={{ zIndex: 13001 }}>
                    <IconButton onClick={zoomIn} sx={ctrlSx}><AddRoundedIcon sx={{ color: 'white' }} /></IconButton>
                </Tooltip>
                <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.15)", mx: 0.5 }} />
                <Tooltip title="Повернуть влево" size="sm" sx={{ zIndex: 13001 }}>
                    <IconButton onClick={rotateL} sx={ctrlSx}><RotateLeftRoundedIcon sx={{ color: 'white' }} /></IconButton>
                </Tooltip>
                <Tooltip title="Повернуть вправо" size="sm" sx={{ zIndex: 13001 }}>
                    <IconButton onClick={rotateR} sx={ctrlSx}><RotateRightRoundedIcon sx={{ color: 'white' }} /></IconButton>
                </Tooltip>
                <Tooltip title="Сбросить" size="sm" sx={{ zIndex: 13001 }}>
                    <IconButton onClick={reset} sx={ctrlSx}><RestartAltRoundedIcon sx={{ color: 'white' }} /></IconButton>
                </Tooltip>
                <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.15)", mx: 0.5 }} />
                <Tooltip title="Скачать" size="sm" sx={{ zIndex: 13001 }}>
                    <IconButton component="a" href={cur.url} download={cur.name || ""}
                        target="_blank" rel="noreferrer" sx={ctrlSx}>
                        <DownloadRoundedIcon sx={{ color: 'white' }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </motion.div>
    );

    return createPortal(node, document.body);
}